import { useEffect, useState } from 'react';
import type { Escala, SetImagenes } from '@copathy/shared';
import { Boton } from './Boton';
import { Campo } from './Campo';
import { ImagenCuestionario } from './ImagenCuestionario';
import { Modal } from './Modal';
import { Equis } from './iconos';
import { ErrorApi } from '../lib/api';

/**
 * Crear y editar una escala: su nombre y **el significado de cada punto**.
 *
 * ── Por qué existe esta pantalla ──
 * No había ninguna (D-134). `POST /api/cuestionarios/escalas` funcionaba y tenía
 * sus pruebas, pero sin interfaz: las dos escalas que hay entraron por migración
 * y el constructor solo dejaba elegir entre ellas. O sea que **no se podía
 * corregir el significado de un punto desde la plataforma**, y desde que ese
 * significado se muestra al elegir (D-131) una etiqueta floja se ve.
 *
 * ⚠️ **Una escala que ya usa una versión publicada NO se edita.** Lo impide el
 * motor —`tg_escala_puntos_inmutable`, migración 011— porque cambiarle el
 * significado a un punto cambiaría lo que la gente ya respondió. Acá se dice
 * antes de intentarlo y se ofrece la salida real: duplicarla y editar la copia.
 */
type Punto = { etiqueta: string; emoji: string };

const VACIO: Punto = { etiqueta: '', emoji: '' };

export function ModalEscala({
  abierto,
  escala,
  alCerrar,
  alGuardar,
  alDesactivar,
  sets,
  alAdministrarSets,
}: {
  abierto: boolean;
  /** `null` para crear una nueva. */
  escala: Escala | null;
  alCerrar: () => void;
  alGuardar: (datos: {
    nombre: string;
    setImagenesId: number | null;
    puntos: { etiqueta: string; emoji: string | null }[];
  }) => Promise<void>;
  /** Los sets disponibles, para poder elegir uno en vez de los emojis. */
  sets: SetImagenes[];
  /** Abre la pantalla de sets: es donde se cargan las figuras. */
  alAdministrarSets: () => void;
  /** Sacarla de la lista de elegibles. Sin esto, una escala mal escrita se queda. */
  alDesactivar?: (() => Promise<void>) | undefined;
}) {
  const [nombre, setNombre] = useState('');
  const [setElegido, setSetElegido] = useState<number | null>(null);
  const [puntos, setPuntos] = useState<Punto[]>([VACIO, VACIO]);
  const [error, setError] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);

  /*
    Al abrir se carga lo que hay. Depende de `abierto` a propósito: si dependiera
    solo de `escala`, reabrir el modal después de cancelar mostraría lo que el
    usuario había escrito y descartado.
  */
  useEffect(() => {
    if (!abierto) return;
    setError(null);
    setNombre(escala?.nombre ?? '');
    setSetElegido(escala?.setImagenesId ?? null);
    setPuntos(
      escala
        ? escala.puntos.map((p) => ({ etiqueta: p.etiqueta, emoji: p.emoji ?? '' }))
        : [VACIO, VACIO],
    );
  }, [abierto, escala]);

  const bloqueada = escala !== null && escala.usosPublicados > 0;

  const cambiar = (i: number, campo: keyof Punto, valor: string) =>
    setPuntos((antes) => antes.map((p, j) => (j === i ? { ...p, [campo]: valor } : p)));

  const quitar = (i: number) => setPuntos((antes) => antes.filter((_, j) => j !== i));

  async function guardar() {
    setError(null);

    const limpios = puntos.map((p) => ({
      etiqueta: p.etiqueta.trim(),
      emoji: p.emoji.trim() || null,
    }));

    /*
      Se valida acá antes de mandar, con el mismo criterio que el esquema de Zod
      del servidor —que es el que de verdad decide—. Avisar antes evita que el
      error llegue como un 422 genérico sin decir cuál de los cinco puntos está
      en blanco.
    */
    if (nombre.trim().length < 3) {
      setError('Ponle un nombre reconocible a la escala.');
      return;
    }
    if (limpios.length < 2) {
      setError('Una escala necesita al menos dos puntos.');
      return;
    }
    const enBlanco = limpios.findIndex((p) => p.etiqueta.length === 0);
    if (enBlanco >= 0) {
      setError(
        `Al punto ${enBlanco + 1} le falta su significado. ` +
          'Es lo que el trabajador lee cuando lo elige, así que ninguno puede quedar en blanco.',
      );
      return;
    }

    /*
      La cantidad se comprueba acá además de en el servidor. No es duplicar por
      duplicar: el servidor devuelve un 422 con el número, pero avisar antes
      evita que alguien escriba cinco significados para que le digan que el set
      tiene tres.
    */
    const elegido = sets.find((x) => x.id === setElegido);
    if (elegido && elegido.imagenes.length !== limpios.length) {
      setError(
        `«${elegido.nombre}» tiene ${elegido.imagenes.length} ` +
          `${elegido.imagenes.length === 1 ? 'imagen' : 'imágenes'} y esta escala tiene ` +
          `${limpios.length} puntos. Se reparten en orden, así que tienen que ser la misma ` +
          'cantidad: agrégale imágenes al set, o ajusta los puntos.',
      );
      return;
    }

    setGuardando(true);
    try {
      await alGuardar({ nombre: nombre.trim(), setImagenesId: setElegido, puntos: limpios });
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : 'No pudimos guardar la escala.');
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Modal
      titulo={escala ? `Escala: ${escala.nombre}` : 'Nueva escala'}
      ancho="ancho"
      abierto={abierto}
      alCerrar={alCerrar}
      pie={
        <div className="flex flex-wrap items-center justify-end gap-2">
          {/*
            Desactivar va a la IZQUIERDA y separado de los otros dos: es la
            acción que no se deshace sola, y pegarla a «Guardar» invita al clic
            equivocado. Se ofrece aunque la escala esté bloqueada para editar,
            porque desactivar no cambia lo que ya se respondió.
          */}
          {escala && alDesactivar && (
            <button
              type="button"
              onClick={() => {
                if (!confirm(`¿Sacar «${escala.nombre}» de la lista de escalas elegibles?`)) return;
                void alDesactivar();
              }}
              className="mr-auto rounded-full px-3.5 py-2.5 text-sm font-semibold text-marino-500
                transition hover:text-red-600 hover:underline
                focus:outline-none focus-visible:ring-4 focus-visible:ring-marino-700/15"
            >
              Desactivar
            </button>
          )}
          <Boton variante="secundario" className="px-4 py-2.5 text-sm" onClick={alCerrar}>
            Cancelar
          </Boton>
          <Boton
            className="px-4 py-2.5 text-sm"
            disabled={bloqueada}
            cargando={guardando}
            onClick={() => void guardar()}
          >
            Guardar
          </Boton>
        </div>
      }
    >
      {/*
        El aviso va ARRIBA y antes de los campos, no como un error al guardar:
        dejar escribir cinco significados para rechazarlos al final es la peor
        forma de comunicar una restricción.
      */}
      {bloqueada && (
        <div
          role="alert"
          className="mb-4 rounded-2xl border border-marino-200 bg-marino-50 px-4 py-3 text-sm text-marino-700"
        >
          {/*
            ⚠️ `usosPublicados` cuenta **preguntas**, no cuestionarios: la
            consulta es `count(*) FROM preguntas WHERE escala_id = … AND
            v.estado <> 'borrador'`. Decía «12 cuestionarios publicados» de
            un solo cuestionario con 12 preguntas — un número con el
            sustantivo equivocado hace dudar de todo lo demás que se lee.
          */}
          <strong className="font-semibold">Esta escala no se puede cambiar.</strong>{' '}
          {escala?.usosPublicados === 1
            ? 'La usa una pregunta ya publicada'
            : `La usan ${escala?.usosPublicados} preguntas ya publicadas`}
          , y el significado de cada punto es parte de lo que la gente respondió. Si necesitas otra
          parecida, crea una nueva con los cambios: las respuestas viejas siguen apuntando a esta.
        </div>
      )}

      <div className="space-y-4">
        <Campo
          etiqueta="Nombre de la escala"
          value={nombre}
          disabled={bloqueada}
          onChange={(e) => setNombre(e.target.value)}
        />

        {/*
          ── Con qué se dibuja la escala ──
          Los emojis de cada punto, o un set de imágenes propias (CC-04). El
          selector va ARRIBA de los puntos porque cambia qué significa la
          columna de la derecha: con un set, el emoji de cada punto deja de
          usarse y lo que manda es la figura que le toca por orden.
        */}
        <fieldset disabled={bloqueada}>
          <legend className="mb-1.5 text-sm font-semibold text-marino-700">
            Con qué se dibuja cada punto
          </legend>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={setElegido ?? ''}
              onChange={(e) => setSetElegido(e.target.value === '' ? null : Number(e.target.value))}
              className="rounded-2xl border border-marino-200 bg-white px-3.5 py-2.5 text-sm
                font-medium text-marino-800 disabled:opacity-50
                focus:outline-none focus:ring-4 focus:ring-marino-700/10"
            >
              <option value="">Los emojis de cada punto</option>
              {sets
                .filter((x) => x.activo && x.imagenes.length > 0)
                .map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.nombre} · {x.imagenes.length}{' '}
                    {x.imagenes.length === 1 ? 'imagen' : 'imágenes'}
                  </option>
                ))}
            </select>
            <button
              type="button"
              onClick={alAdministrarSets}
              className="rounded-full px-3.5 py-2 text-sm font-semibold text-marino-500
                transition hover:text-marino-800 hover:underline
                focus:outline-none focus-visible:ring-4 focus-visible:ring-marino-700/15"
            >
              Administrar sets
            </button>
          </div>

          {/*
            Las figuras del set elegido, a la vista y numeradas. Elegir un set de
            una lista de nombres es elegir a ciegas: lo que importa es si esas
            caritas son las que se quieren, y en qué orden.
          */}
          {setElegido !== null && (
            <ul className="mt-2.5 flex flex-wrap gap-2">
              {(sets.find((x) => x.id === setElegido)?.imagenes ?? []).map((img) => (
                <li key={img.id} className="relative">
                  <span className="block overflow-hidden rounded-xl border border-marino-100 bg-white">
                    <ImagenCuestionario
                      ruta={`/api/cuestionarios/sets/imagenes/${img.id}`}
                      hay
                      alt={`Punto ${img.orden}`}
                      className="h-12 w-12"
                    />
                  </span>
                  <span className="absolute -left-1 -top-1 grid h-4 w-4 place-items-center rounded-full bg-marino-800 text-[10px] font-bold text-white">
                    {img.orden}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </fieldset>

        <div>
          <p className="mb-1.5 text-sm font-semibold text-marino-700">
            Los puntos, del primero al último
          </p>
          {/*
            Se dice para qué sirve el significado. «Etiqueta» a secas no explica
            que es el texto que aparece debajo cuando alguien elige ese punto, y
            sin saberlo se escriben cosas como «opción 3».
          */}
          <p className="mb-3 text-xs text-marino-400">
            El significado es lo que el trabajador lee cuando elige ese punto.{' '}
            {setElegido === null
              ? 'El emoji es opcional: si lo pones, se ve en vez del número.'
              : 'Con un set elegido, cada punto se dibuja con su figura y el emoji no se usa.'}
          </p>

          <ul className="space-y-2">
            {puntos.map((p, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="mt-3 grid h-7 w-7 shrink-0 place-items-center rounded-full bg-marino-50 text-xs font-bold text-marino-500">
                  {i + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <Campo
                    etiqueta={`Significado del punto ${i + 1}`}
                    value={p.etiqueta}
                    disabled={bloqueada}
                    onChange={(e) => cambiar(i, 'etiqueta', e.target.value)}
                  />
                </div>
                <div className="w-24 shrink-0">
                  <Campo
                    etiqueta="Emoji"
                    value={p.emoji}
                    disabled={bloqueada}
                    maxLength={16}
                    onChange={(e) => cambiar(i, 'emoji', e.target.value)}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => quitar(i)}
                  disabled={bloqueada || puntos.length <= 2}
                  aria-label={`Quitar el punto ${i + 1}`}
                  title={puntos.length <= 2 ? 'Una escala necesita al menos dos puntos' : 'Quitar'}
                  className="mt-3 grid h-8 w-8 shrink-0 place-items-center rounded-full text-marino-400
                    transition hover:bg-marino-50 hover:text-red-600
                    disabled:cursor-not-allowed disabled:opacity-25
                    focus:outline-none focus-visible:ring-4 focus-visible:ring-marino-700/15"
                >
                  <Equis size={14} />
                </button>
              </li>
            ))}
          </ul>

          <button
            type="button"
            onClick={() => setPuntos((antes) => [...antes, { ...VACIO }])}
            disabled={bloqueada || puntos.length >= 11}
            title={puntos.length >= 11 ? 'Más de once puntos deja de leerse como una escala' : ''}
            className="mt-3 rounded-full border border-marino-200 bg-white px-3.5 py-1.5 text-sm
              font-semibold text-marino-700 transition hover:bg-marino-50
              disabled:cursor-not-allowed disabled:opacity-40
              focus:outline-none focus-visible:ring-4 focus-visible:ring-marino-700/15"
          >
            Agregar punto
          </button>
        </div>

        {error && (
          <p role="alert" className="text-sm font-medium text-red-600">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
